import { API_PATHS, type Proof } from '@uroute/shared';
import { useEffect, useState } from 'react';
import { tokenStore } from '../lib/api';
import { FileText } from './icons';

export interface ProofThumbProps {
  proof: Proof;
  size?: 'sm' | 'lg';
}

/** Fetches a proof's file once and shows a small thumbnail. */
export function ProofThumb({ proof, size = 'sm' }: ProofThumbProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    let objectUrl: string | null = null;
    void (async () => {
      try {
        const headers: Record<string, string> = {};
        const token = tokenStore.access();
        if (token) headers.Authorization = `Bearer ${token}`;
        const res = await fetch(`${API_PATHS.proofs}/${proof.id}/file`, { headers });
        if (!res.ok) throw new Error(String(res.status));
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        if (alive) setUrl(objectUrl);
        else URL.revokeObjectURL(objectUrl);
      } catch {
        if (alive) setFailed(true);
      }
    })();
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [proof.id]);

  const alt = `${proof.type} proof ${proof.originalFilename}`;

  return (
    <div
      className={`proofthumb proofthumb--${size}`}
      data-testid="proof-thumb"
      data-proof-id={proof.id}
      data-proof-type={proof.type}
    >
      {url ? (
        <img src={url} alt={alt} />
      ) : failed ? (
        <span className="proofthumb__placeholder" aria-label="Proof unavailable offline">
          <FileText size={size === 'lg' ? 28 : 16} />
        </span>
      ) : (
        <span className="proofthumb__placeholder" aria-hidden="true" />
      )}
    </div>
  );
}
